// Frame-to-frame driver for MIDI-out playback (F1). Each RAF frame it's handed the
// desired send-set (engine.soundingOut) + the playhead; it diffs against the previous
// frame and emits note-on/off, mirroring how HeldKeys diffs the input. Engine-unaware:
// main.ts samples engine.soundingOut and feeds it here. The single-clock invariant holds
// — there's no scheduler, just a per-frame diff off the one RAF clock.
import type { MidiSink } from "./output";

/** A playhead delta larger than this (or any rewind) is a seek / restart / loop-wrap /
 *  coach-window jump, not continuous play — panic so notes re-articulate cleanly and
 *  nothing hangs across the discontinuity. Generous vs a real frame (~16 ms × tempo). */
const MAX_CONTINUOUS_JUMP_S = 0.5;

export class PlaybackOut {
  /** Last frame's desired set: midi → velocity. */
  private prev = new Map<number, number>();
  private lastPlayhead = Number.NEGATIVE_INFINITY;

  constructor(private readonly sink: MidiSink) {}

  /** Reconcile the sink to `desired` for this frame. */
  sync(desired: { midi: number; velocity: number }[], playhead: number): void {
    if (playhead < this.lastPlayhead || playhead - this.lastPlayhead > MAX_CONTINUOUS_JUMP_S) {
      this.sink.panic();
      this.prev.clear();
    }
    this.lastPlayhead = playhead;

    const want = new Map(desired.map((d) => [d.midi, d.velocity] as const));
    for (const midi of this.prev.keys()) if (!want.has(midi)) this.sink.noteOff(midi);
    for (const [midi, vel] of want) if (!this.prev.has(midi)) this.sink.noteOn(midi, vel);
    this.prev = want;
  }

  /** Stop all sound and reset. Wired in main.ts to pause/stop, mode/hand/transpose/song
   *  change, output deselect, MIDI disconnect, and accompaniment toggle-off. */
  silence(): void {
    this.sink.panic();
    this.prev.clear();
    this.lastPlayhead = Number.NEGATIVE_INFINITY;
  }
}
